import * as React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { createVercelProject } from "@/lib/api/vercelProjects";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { useParams } from "react-router-dom";

interface CreateVercelProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (data: any) => void;
}

export function CreateVercelProjectModal({
  open,
  onOpenChange,
  onSuccess,
}: CreateVercelProjectModalProps) {
  const [projectName, setProjectName] = React.useState("");
  const [localError, setLocalError] = React.useState<string | null>(null);
  const {siteSlug} = useParams<{ siteSlug: string }>("");
  const mutation = useMutation({
    mutationFn: async (name: string) => {
      setLocalError(null);
      return await createVercelProject(siteSlug, name);
    },
    onSuccess: (data) => {
      if (data && data.errors) {
        toast.error(
          "Failed to create Vercel project",
          {
            description: Array.isArray(data.errors)
              ? data.errors.join(", ")
              : String(data.errors),
          }
        );
      } else {
        toast("Vercel project created");
        setProjectName("");
        onOpenChange(false);
        if (onSuccess) onSuccess(data);
      }
    },
    onError: (error: any) => {
      setLocalError(error?.message || "Unknown error");
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!projectName.trim()) {
      setLocalError("Project name is required");
      return;
    }
    mutation.mutate(projectName.trim());
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setProjectName("");
      setLocalError(null);
    }
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Vercel Project</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            placeholder="Vercel project name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            disabled={mutation.status === "pending"}
            autoFocus
          />
          {localError && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{localError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button
              type="submit"
            disabled={mutation.status === "pending" || !projectName.trim()}
          >
              {mutation.status === "pending" && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            disabled={mutation.status === "pending"}
          >
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
